import { Accordion, AccordionSummary, Typography, AccordionDetails, Box } from "@mui/material";
import { ReactNode, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAppTheme } from "@/hooks/useAppTheme";

interface ShowAccordionSectionProps {
  label: string;
  children: ReactNode;
  defaultExpanded?: boolean;
  bgColor?: boolean;
  actions?: ReactNode;
}

export const ShowAccordionSection = ({
  label,
  children,
  defaultExpanded = false,
  bgColor = true,
  actions,
}: ShowAccordionSectionProps) => {
  const { theme } = useAppTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        mb: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "12px !important",
        overflow: "hidden", // Clip children to border radius
        "&:before": {
          display: "none",
        },
        // Target the root element
        "&.MuiAccordion-root": {
          borderRadius: "12px",
        },
        // Ensure first and last children have proper border radius
        "&:first-of-type": {
          borderRadius: "12px",
        },
        "&:last-of-type": {
          borderRadius: "12px",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderRadius: expanded ? "12px 12px 0 0" : "12px", // Round top corners, or all if collapsed
          "& .MuiAccordionSummary-content": {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          },
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {label}
        </Typography>
        {actions && (
          <Box
            onClick={(e) => e.stopPropagation()} // Prevent accordion toggle when clicking actions
            sx={{ ml: 2, mr: 2 }}
          >
            {actions}
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          boxShadow: "none",
          borderTop: `1px solid ${theme.palette.divider}`,
          borderRadius: "0 0 12px 12px", // Round bottom corners
          px: 2,
          py: 1,
        }}
      >
        <Box
          sx={{
            backgroundColor: bgColor ? theme.palette.background.paper : "none",
            overflowX: "auto", // enable horizontal scroll if needed
            width: "100%", // prevent overflowing container
            maxWidth: "100%", // keep it contained in parent layout
            boxSizing: "border-box", // ensure padding/margins don't break width
          }}
        >
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
